import { memoize } from "lodash";
import {
  ADMIN_EVENT_COUNTS_DEFAULTS,
  ADMIN_DEFAULT_ISSUES_COUNT,
  SUPERVISOR_CURRENT_MONTH_DEFAULTS,
  CONFIGURATION_RECEIPT_DEFAULTS,
  CURRENT_MONTH_CALCULATION_ARRAY,
  ADMIN_AUDITED_EVENT_COUNTS_DEFAULTS
} from "../../../constants/input-entries-constants";
import { apiCallGetCSRReceiptCompensation } from "../../../api/csr-api/api-call-get-csr-compensation";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { computed, observable, runInAction } from "mobx";
import {
  TAdminFinalReceipts,
  TCurrentMonth,
  TDataInputObject,
  TConfigurationReceipt
} from "../../../Types";
import { getMonthAndYear } from "../../../helpers/et3-date-helper";
import { apiCallGetFinalCSRReceipt } from "../../../api/csr-api/api-call-get-final-csr-salary-objects";
import {
  calculateAndUpdateCompensationDiscount,
  getReceiptFinalSalary
} from "../../../helpers/et3-receipt-compensation-and-calculation/receipt-compensation-calculation";
import { reduce } from "et3-type-guards";

class CSRReceiptStore {
  currentMonthCSRReceipt = observable.box<TCurrentMonth>();
  adminFinalReceipts = observable.box<TAdminFinalReceipts>([]);
  dataCSRInputReceipt = observable.box<TDataInputObject>();
  configurationReceipt = observable.box<TConfigurationReceipt>(
    CONFIGURATION_RECEIPT_DEFAULTS
  );
  finalCSRSalary = observable.box<number>(0);

  finalSalary = computed(() => {
    const supervisorCompensation = this.currentMonthCSRReceipt.get();
    const calculatedSalary =
      supervisorCompensation.description.price +
      supervisorCompensation.description.discount +
      supervisorCompensation.report.price +
      supervisorCompensation.report.discount +
      supervisorCompensation.picture.price +
      supervisorCompensation.picture.discount +
      supervisorCompensation.investigationCases.price +
      supervisorCompensation.investigationCases.discount;
    return calculatedSalary;
  });

  setFinalReceiptDashboard = (data: TAdminFinalReceipts) => {
    runInAction(() => {
      const finalData = data.map((item) => {
        return {
          ...item,
          createdOn: getMonthAndYear(item.createdOn)
        };
      });
      this.adminFinalReceipts.set(finalData);
    });
  };

  getCSRReceiptCompensation = async () => {
    getCoreUIStore().setIsLoading("CSR_MY_RECEIPT_EVENT_COUNT", true);
    const { status, data, code } = await apiCallGetCSRReceiptCompensation();
    getCoreUIStore().setIsLoading("CSR_MY_RECEIPT_EVENT_COUNT", false);
    const eventsCount = reduce(
      () => data.eventsCount,
      ADMIN_EVENT_COUNTS_DEFAULTS
    );
    const auditedEventsCount = reduce(
      () => data.auditedEventsCount,
      ADMIN_AUDITED_EVENT_COUNTS_DEFAULTS
    );
    const adminIssuesCount = reduce(
      () => data.adminIssuesCount,
      ADMIN_DEFAULT_ISSUES_COUNT
    );
    const configurationReceipt = reduce(
      () => data.configurationReceipt,
      CONFIGURATION_RECEIPT_DEFAULTS
    );
    const adminFinalReceipts = reduce(() => data.adminFinalReceipts, []);
    const dataInputObject = {
      ...eventsCount,
      ...auditedEventsCount,
      ...adminIssuesCount
    };
    if (status === "OK") {
      this.dataCSRInputReceipt.set(dataInputObject);
      this.configurationReceipt.set(configurationReceipt);
      this.setFinalReceiptDashboard(adminFinalReceipts);
      CURRENT_MONTH_CALCULATION_ARRAY.map((objectKey) => {
        const currentMonthReceipt = calculateAndUpdateCompensationDiscount(
          dataInputObject,
          objectKey,
          this.configurationReceipt.get(),
          this.currentMonthCSRReceipt.get()
        );
        this.currentMonthCSRReceipt.set(currentMonthReceipt);
      });
    } else {
      this.dataCSRInputReceipt.set(dataInputObject);
      this.adminFinalReceipts.set([]);
      this.currentMonthCSRReceipt.set(SUPERVISOR_CURRENT_MONTH_DEFAULTS);
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getFinalSalary = async () => {
    getCoreUIStore().setIsLoading("CSR_MY_RECEIPT_EVENT_COUNT", true);
    const { status, data, code } = await apiCallGetFinalCSRReceipt();
    getCoreUIStore().setIsLoading("CSR_MY_RECEIPT_EVENT_COUNT", false);
    const eventsCount = reduce(
      () => data.eventsCount,
      ADMIN_EVENT_COUNTS_DEFAULTS
    );
    const auditedEventsCount = reduce(
      () => data.auditedEventsCount,
      ADMIN_AUDITED_EVENT_COUNTS_DEFAULTS
    );
    const adminIssuesCount = reduce(
      () => data.adminIssuesCount,
      ADMIN_DEFAULT_ISSUES_COUNT
    );
    const configurationReceipt = reduce(
      () => data.configurationReceipt,
      CONFIGURATION_RECEIPT_DEFAULTS
    );
    const dataInputObject = {
      ...eventsCount,
      ...auditedEventsCount,
      ...adminIssuesCount
    };
    if (status === "OK") {
      const finalSalary = getReceiptFinalSalary(
        dataInputObject,
        configurationReceipt
      );
      this.finalCSRSalary.set(finalSalary);
    } else {
      getCorePopUpStore().showAlertMessage(code);
      this.finalCSRSalary.set(0);
    }
  };
}
export const getCSRReceiptStore = memoize(
  () => new CSRReceiptStore(),
  () => 1
);
export default getCSRReceiptStore;
